import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Loader2, Briefcase, Clock, ChevronRight, Plus, LogOut, ArrowRight, Trash2,
  TrendingUp, Lock, MessageSquare, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { StaffBottomNav, StaffHeader } from '@/components/staff/StaffNav';
import { InstallStaffPwaCard } from '@/components/staff/InstallStaffPwaCard';
import { useStaffAuth } from '@/contexts/StaffAuthContext';
import {
  meDashboard, meProjects, meHours, meLogHours, meDeleteHours, meUpdateProfile, fmtErr,
} from '@/lib/staffApi';

/* ── Layout shell ─────────────────────────────────────────────── */
function StaffShell({ title, action, children }) {
  return (
    <div className="min-h-screen bg-[#fbfaf7]" data-testid="staff-shell">
      <StaffHeader title={title} action={action} />
      <main className="max-w-2xl mx-auto px-4 py-5 pb-24" data-testid="staff-main">
        {children}
      </main>
      <StaffBottomNav />
    </div>
  );
}

function PermissionBadge({ level }) {
  const map = {
    full:      { label: 'Full access',  icon: TrendingUp,   tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    commenter: { label: 'Comment only', icon: MessageSquare, tone: 'bg-blue-50 text-blue-700 border-blue-200' },
    limited:   { label: 'View only',    icon: Eye,           tone: 'bg-muted text-muted-foreground border-border' },
  };
  const cfg = map[level] || map.limited;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={`gap-1 text-[10px] ${cfg.tone}`} data-testid={`perm-badge-${level}`}>
      <Icon className="h-2.5 w-2.5" />{cfg.label}
    </Badge>
  );
}

/* ── Home ─────────────────────────────────────────────────────── */
export function StaffHomePage() {
  const { user } = useStaffAuth();
  const [data, setData] = useState(null);
  useEffect(() => { meDashboard().then(setData).catch(e => toast.error(fmtErr(e))); }, []);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  }, []);

  return (
    <StaffShell title="Flex Living Miami">
      <div className="space-y-5">
        <div data-testid="staff-greeting">
          <p className="text-xs text-muted-foreground">{greeting},</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{user?.name?.split(' ')[0] || 'there'} 👋</h1>
        </div>

        <InstallStaffPwaCard />

        {!data ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3" data-testid="staff-stat-cards">
              <Link to="/staff/pay-periods" className="block" data-testid="stat-period-link">
                <Card className="bg-emerald-900 text-white border-0 hover:bg-emerald-800 transition-colors h-full">
                  <CardContent className="p-4">
                    <p className="text-[11px] uppercase tracking-wider text-emerald-300">This pay period</p>
                    <p className="text-3xl font-bold tabular-nums mt-1" data-testid="stat-period-hours">{(data.hours_this_period ?? 0).toFixed(1)}<span className="text-sm font-normal opacity-70 ml-1">hrs</span></p>
                    <p className="text-[11px] text-emerald-200 mt-1">vs {(data.hours_last_period ?? 0).toFixed(1)} last period</p>
                  </CardContent>
                </Card>
              </Link>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <p className="text-[11px] uppercase tracking-wider text-amber-700">My projects</p>
                  <p className="text-3xl font-bold text-amber-900 tabular-nums mt-1" data-testid="stat-project-count">{data.project_count}</p>
                  <p className="text-[11px] text-amber-700 mt-1">{data.editable_count} editable</p>
                </CardContent>
              </Card>
            </div>

            <Card className="border-emerald-200" data-testid="staff-quick-log-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                  <Plus className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">Log time</p>
                  <p className="text-xs text-muted-foreground">Quick entry on any of your projects</p>
                </div>
                <Button size="sm" asChild data-testid="staff-quick-log-btn"><Link to="/staff/hours?log=1">Log</Link></Button>
              </CardContent>
            </Card>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-heading font-semibold text-sm">My projects</h2>
                <Link to="/staff/projects" className="text-xs text-emerald-700 hover:underline" data-testid="staff-home-see-all">See all</Link>
              </div>
              <ProjectListPreview />
            </div>
          </>
        )}
      </div>
    </StaffShell>
  );
}

function ProjectListPreview() {
  const [projects, setProjects] = useState(null);
  useEffect(() => { meProjects().then(setProjects).catch(() => setProjects([])); }, []);
  if (!projects) return <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-emerald-600" /></div>;
  if (projects.length === 0) return <p className="text-xs text-muted-foreground py-4 text-center">No projects assigned yet.</p>;
  return (
    <div className="space-y-2" data-testid="staff-project-preview-list">
      {projects.slice(0, 3).map(p => <ProjectCard key={p.id} project={p} />)}
    </div>
  );
}

function ProjectCard({ project }) {
  return (
    <Link
      to={`/staff/projects/${project.id}`}
      className="block rounded-xl border border-border/70 bg-card p-3.5 hover:border-emerald-300 hover:shadow-sm transition-all"
      data-testid="staff-project-card"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <Briefcase className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{project.name}</p>
          <p className="text-xs text-muted-foreground truncate">{project.address || 'No address'}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <PermissionBadge level={project.permission_level} />
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </Link>
  );
}

/* ── Projects list ────────────────────────────────────────────── */
export function StaffProjectsPage() {
  const [projects, setProjects] = useState(null);
  useEffect(() => { meProjects().then(setProjects).catch(e => { toast.error(fmtErr(e)); setProjects([]); }); }, []);

  return (
    <StaffShell title="My Projects">
      {!projects ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
      ) : projects.length === 0 ? (
        <div className="py-16 text-center">
          <Briefcase className="h-10 w-10 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No projects assigned yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Ask your admin to add you to a project.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="staff-project-list">
          {projects.map(p => <ProjectCard key={p.id} project={p} />)}
        </div>
      )}
    </StaffShell>
  );
}

/* ── Hours ────────────────────────────────────────────────────── */
export function StaffHoursPage() {
  const [data, setData] = useState(null);
  const [showLog, setShowLog] = useState(typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('log') === '1');

  const refresh = () => meHours().then(setData).catch(e => toast.error(fmtErr(e)));
  useEffect(() => { refresh(); }, []);

  const groups = useMemo(() => {
    if (!data?.rows) return [];
    const map = new Map();
    data.rows.forEach(r => {
      if (!map.has(r.date)) map.set(r.date, []);
      map.get(r.date).push(r);
    });
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [data]);

  return (
    <StaffShell
      title="My Hours"
      action={
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => setShowLog(true)} data-testid="staff-hours-log-btn">
          <Plus className="h-3.5 w-3.5 mr-1" />Log
        </Button>
      }
    >
      {!data ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
      ) : (
        <div className="space-y-4">
          <Card className="bg-emerald-900 text-white border-0">
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wider text-emerald-300">Last 14 days</p>
              <p className="text-4xl font-bold tabular-nums mt-1" data-testid="stat-total-hours">
                {data.total_hours.toFixed(2)}<span className="text-base font-normal opacity-70 ml-1">hrs</span>
              </p>
              <p className="text-[11px] text-emerald-200 mt-1">{data.rows.length} {data.rows.length === 1 ? 'entry' : 'entries'}</p>
            </CardContent>
          </Card>

          {groups.length === 0 ? (
            <div className="py-16 text-center" data-testid="hours-empty">
              <Clock className="h-10 w-10 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No hours logged yet.</p>
              <Button size="sm" className="mt-3" onClick={() => setShowLog(true)}>Log your first hours</Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="staff-hours-list">
              {groups.map(([date, rows]) => (
                <DayCard key={date} date={date} rows={rows} onChange={refresh} />
              ))}
            </div>
          )}
        </div>
      )}

      <LogHoursDialog open={showLog} onOpenChange={setShowLog} onLogged={refresh} />
    </StaffShell>
  );
}

function DayCard({ date, rows, onChange }) {
  const total = rows.reduce((s, r) => s + r.hours, 0);
  return (
    <div className="rounded-xl border border-border/70 bg-card overflow-hidden" data-testid="hours-day-card">
      <div className="px-3.5 py-2 bg-muted/40 border-b border-border/60 flex items-center justify-between">
        <p className="text-xs font-semibold">{new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
        <p className="text-xs font-semibold text-emerald-700 tabular-nums">{total.toFixed(2)} hrs</p>
      </div>
      <div className="divide-y divide-border/60">
        {rows.map(r => <HoursRow key={r.id} row={r} onChange={onChange} />)}
      </div>
    </div>
  );
}

function HoursRow({ row, onChange }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm('Delete this hours entry?')) return;
    setBusy(true);
    try { await meDeleteHours(row.id, row.project_id); toast.success('Removed'); onChange(); }
    catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const fmt = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ap = h >= 12 ? 'pm' : 'am';
    return `${h % 12 || 12}:${String(m).padStart(2, '0')}${ap}`;
  };
  return (
    <div className="px-3.5 py-2.5 flex items-center gap-3" data-testid="hours-row">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{row.project_name}</p>
        <p className="text-[11px] text-muted-foreground truncate">{fmt(row.start_time)} – {fmt(row.end_time)}{row.notes ? ` · ${row.notes}` : ''}</p>
      </div>
      <p className="text-sm font-semibold tabular-nums">{row.hours.toFixed(2)} h</p>
      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" disabled={busy} onClick={remove} data-testid="hours-delete">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
      </Button>
    </div>
  );
}

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function LogHoursDialog({ open, onOpenChange, onLogged }) {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ project_id: '', date: new Date().toISOString().slice(0, 10), start_time: '09:00', end_time: '09:00', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    meProjects().then(p => {
      setProjects(p);
      setForm(f => ({
        ...f,
        project_id: p[0]?.id || '',
        date: new Date().toISOString().slice(0, 10),
        start_time: '09:00',
        end_time: nowHHMM() > '09:00' ? nowHHMM() : '09:00',
        notes: '',
      }));
    }).catch(() => {});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error('Pick a project'); return; }
    setSaving(true);
    try {
      await meLogHours(form);
      toast.success('Hours logged');
      onLogged();
      onOpenChange(false);
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="staff-log-hours-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" />Log Hours</DialogTitle>
          <DialogDescription>Quick entry — assigned to your account.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Project</Label>
            <Select value={form.project_id} onValueChange={v => setForm(f => ({ ...f, project_id: v }))}>
              <SelectTrigger data-testid="log-project"><SelectValue placeholder="Pick a project…" /></SelectTrigger>
              <SelectContent>
                {projects.length === 0 ? <SelectItem value="__none" disabled>No projects assigned</SelectItem>
                  : projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Date</Label>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required data-testid="log-date" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Start</Label>
              <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} required data-testid="log-start" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">End</Label>
              <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} required data-testid="log-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Notes (optional)</Label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="What did you work on?" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring resize-y" data-testid="log-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="log-submit" className="bg-emerald-700 hover:bg-emerald-800">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ArrowRight className="h-4 w-4 mr-1" />}Log
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── Profile ──────────────────────────────────────────────────── */
export function StaffProfilePage() {
  const { user, refresh, logout } = useStaffAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [pw, setPw] = useState({ current: '', next: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => { setName(user?.name || ''); }, [user]);

  const saveName = async () => {
    if (!name || name === user?.name) return;
    setBusy(true);
    try { await meUpdateProfile({ name }); await refresh(); toast.success('Saved'); }
    catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    if (!pw.current || pw.next.length < 8) { toast.error('New password must be at least 8 chars'); return; }
    setBusy(true);
    try {
      await meUpdateProfile({ current_password: pw.current, new_password: pw.next });
      toast.success('Password updated');
      setPw({ current: '', next: '' });
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const signOut = async () => {
    await logout();
    navigate('/staff/login', { replace: true });
  };

  return (
    <StaffShell title="My Profile">
      <div className="space-y-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold text-lg flex-shrink-0">
              {(user?.name || user?.email || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate" data-testid="profile-display-name">{user?.name || '—'}</p>
              <p className="text-xs text-muted-foreground truncate" data-testid="profile-email">{user?.email}</p>
              <p className="text-[11px] text-muted-foreground capitalize mt-0.5">{user?.role} · {user?.permission_level || 'limited'}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Display name</p>
            <div className="flex gap-2">
              <Input value={name} onChange={e => setName(e.target.value)} data-testid="profile-name-input" />
              <Button onClick={saveName} disabled={busy || name === user?.name} data-testid="profile-save-name">Save</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3 flex items-center gap-1.5"><Lock className="h-3 w-3" />Change password</p>
            <form onSubmit={changePw} className="space-y-2">
              <Input type="password" placeholder="Current password" value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} data-testid="profile-current-pw" />
              <Input type="password" placeholder="New password (min 8 chars)" value={pw.next} onChange={e => setPw(p => ({ ...p, next: e.target.value }))} data-testid="profile-new-pw" />
              <Button type="submit" disabled={busy || !pw.current || pw.next.length < 8} className="w-full" data-testid="profile-save-pw">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Update password
              </Button>
            </form>
          </CardContent>
        </Card>

        <Button variant="outline" className="w-full text-destructive hover:bg-destructive/5" onClick={signOut} data-testid="profile-signout">
          <LogOut className="h-4 w-4 mr-1.5" />Sign out
        </Button>
      </div>
    </StaffShell>
  );
}
